import { useTranslation } from 'react-i18next'
import { LangText } from '@/components'
import OutLink from '@/components/OutLink'
import { preFixImgUrl } from '@/utils'
import useModel from './useModel'
import { PlatformEnum } from '@/service/api/types'

export const TITLE_GRADIENT = 'bg-linear-to-r from-[#4B51FF] to-[#A75AFF] bg-clip-text text-transparent'

const SPARKLE_IMG_LEFT = 'c8d09d85003dd818aabcc93ae26e2ae7.png'
const SPARKLE_IMG_RIGHT = '1048c33f9e1f3216dcf930bd25478c9f.png'

export const SparkleTitle = ({
  lngKey,
  className,
  purpleTitle,
}: {
  lngKey: string
  className?: string
  purpleTitle?: boolean
}) => {
  const { t } = useTranslation()
  return (
    <div className={`flex items-center justify-center gap-4 ${className ?? ''}`}>
      <img
        src={preFixImgUrl(SPARKLE_IMG_LEFT)}
        alt=""
        className="h-[64px] w-auto object-contain shrink-0 select-none pointer-events-none"
        aria-hidden
      />
      <LangText
        lngKey={lngKey}
        className={`text-[32px] font-bold text-center inline-block ${purpleTitle ? TITLE_GRADIENT : 'text-[#1a1a1a]'}`}
      >
        {t(lngKey)}
      </LangText>
      <img
        src={preFixImgUrl(SPARKLE_IMG_RIGHT)}
        alt=""
        className="h-[64px] w-auto object-contain shrink-0 select-none pointer-events-none"
        aria-hidden
      />
    </div>
  )
}

const HanedaShop = () => {
  const { t } = useTranslation()
  const { featureScreens, processSteps, quickLinks } = useModel()

  const scrollToProcess = () => {
    document.getElementById('haneda-process')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="bg-[#F8F6FF]">
      {/* Hero */}
      <div
        className="relative min-h-[480px] flex flex-col items-center justify-center px-6 py-20 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${preFixImgUrl('8c2b099a6b23067a3b0e8424d0c41eaa.png')})` }}
      >
        <div className="flex relative z-10 flex-col items-center mx-auto max-w-3xl text-center">
          <div className="px-10 py-5 rounded-xl">
            <LangText lngKey="54f65e85" className="text-white text-[40px] md:text-[44px] font-bold leading-tight">
              {t('54f65e85')}
            </LangText>
          </div>
          <LangText
            lngKey="d276d0ec"
            className="text-white text-[17px] md:text-[18px] leading-relaxed px-2"
          >
            {t('d276d0ec')}
          </LangText>
          <div className="flex flex-wrap gap-5 justify-center mt-10">
            <OutLink platform={PlatformEnum.JAPANMALL}>
              <div
                className="h-[48px] min-w-[140px] px-10 flex items-center justify-center rounded-full text-white text-[17px] cursor-pointer transition-opacity hover:opacity-90"
                style={{ background: 'linear-gradient(270deg, #A75AFF 0%, #4B51FF 100%)' }}
              >
                <LangText lngKey="f8a58b1a">{t('f8a58b1a')}</LangText>
              </div>
            </OutLink>
            <button
              type="button"
              onClick={scrollToProcess}
              className="h-[48px] min-w-[140px] px-10 flex items-center justify-center rounded-full text-white text-[17px] border-1 border-white bg-transparent cursor-pointer hover:bg-white/10 transition-colors"
            >
              <LangText lngKey="1fe572a6">{t('1fe572a6')}</LangText>
            </button>
          </div>
        </div>
      </div>

      {/* 流程 6 步 */}
      <section id="haneda-process" className="py-[40px] md:py-[60px]">
        <div className="px-6 mx-auto w-8xl md:px-10">
          <SparkleTitle lngKey="127e05e3" purpleTitle />
          <div className="grid grid-cols-3 gap-x-4 gap-y-14 mt-16 md:mt-20">
            {processSteps.map(({ id, numImg, illustrationImg, titleKey, descKey }) => (
              <div key={id} className="flex flex-col items-center text-center">
                <div className="relative w-full max-w-[260px] md:max-w-[300px] mx-auto">
                  <img
                    src={numImg}
                    alt=""
                    className="absolute z-10 h-11 w-auto max-w-[96px] object-contain object-left-top pointer-events-none left-2 top-0 -translate-x-1 -translate-y-1 md:h-[52px] md:max-w-[112px] md:-translate-x-1.5 md:-translate-y-2"
                    aria-hidden
                  />
                  <div className="rounded-2xl flex items-center justify-center p-4 md:p-5 min-h-[220px] md:min-h-[260px] lg:min-h-[280px]">
                    <img
                      src={illustrationImg}
                      alt=""
                      className="w-full max-h-[240px] md:max-h-[280px] lg:max-h-[300px] object-contain object-center"
                    />
                  </div>
                </div>
                <LangText
                  lngKey={titleKey}
                  className={`inline-block font-bold text-[18px] ${TITLE_GRADIENT}`}
                >
                  {t(titleKey)}
                </LangText>
                <LangText lngKey={descKey} className="text-[15px] text-[#4a4a4a] mt-3 leading-relaxed max-w-[260px]">
                  {t(descKey)}
                </LangText>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 服务特色 */}
      <section className="mt-[20px]">
        <div className="px-6 mx-auto w-8xl md:px-10">
          <SparkleTitle lngKey="402e854a" purpleTitle />
          <div className="mt-10 mb-16 rounded-[28px] bg-white shadow-[0_8px_40px_rgba(0,0,0,0.08)] px-6 py-10 md:px-12 md:py-14">
            <div className="grid grid-cols-4 gap-10 lg:gap-8">
              {featureScreens.map((item) => (
                <div key={item.img} className="flex flex-col items-center text-center">
                  <div className="w-full max-w-[200px] h-auto">
                    <img src={item.img} alt="" className="object-cover w-full h-auto" />
                  </div>
                  <LangText
                    lngKey={item.titleKey}
                    className={`mt-6 font-bold text-[17px] ${TITLE_GRADIENT}`}
                  >
                    {t(item.titleKey)}
                  </LangText>
                  <LangText lngKey={item.subKey} className="text-[15px] text-[#333333] mt-2 leading-snug">
                    {t(item.subKey)}
                  </LangText>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 二次 CTA */}
      <div
        className="relative min-h-[380px] flex flex-col items-center justify-center px-6 py-16 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${preFixImgUrl('b545d25c0f3287f8ba61e70f6f8a13c7.png')})` }}
      >
        <div className="flex relative z-10 flex-col items-center text-center">
          <LangText lngKey="ef341943" className="text-white text-[40px] font-bold">
            {t('ef341943')}
          </LangText>
          <LangText lngKey="661c5382" className="text-white text-[26px] mt-6 font-normal opacity-95">
            {t('661c5382')}
          </LangText>
          <OutLink platform={PlatformEnum.JAPANMALL}>
            <div
              className="h-[52px] min-w-[200px] mt-10 px-10 flex items-center justify-center rounded-full text-white text-[18px] cursor-pointer transition-opacity hover:opacity-90"
              style={{ background: 'linear-gradient(270deg, #A75AFF 0%, #4B51FF 100%)' }}
            >
              <LangText lngKey="be730b5d">{t('be730b5d')}</LangText>
            </div>
          </OutLink>
        </div>
      </div>

      {/* 快捷入口 */}
      <section className="pt-12 pb-16">
        <div className="px-6 mx-auto w-8xl md:px-10">
          <div className="grid grid-cols-4 gap-3 w-full sm:gap-4 md:gap-6">
            {quickLinks.map(({ key, iconImg }) => (
              <div
                key={key}
                className="flex flex-col gap-4 items-center min-w-0 text-center group"
              >
                <span className="w-[88px] h-[88px]">
                  <img src={iconImg} alt="" className="object-contain max-w-full max-h-full" />
                </span>
                <LangText lngKey={key} className="text-[18px] text-[#333333] font-bold">
                  {t(key)}
                </LangText>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default HanedaShop
